import React, { FC, useEffect, useState } from 'react';
import { editProcedurePreOpNote, procedureEditFieldsQuery } from '@/graph/procedures';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { MappedPatient } from '@/components/pages/kiosk/types/RoomTabletTypes';

type ProcedurePreOpNoteInputProps = {
  open: boolean;
  handleClose: () => void;
  patient?: MappedPatient;
  onClick?: (note: string) => void | Promise<void>;
};

const ProcedurePreOpNoteInput: FC<ProcedurePreOpNoteInputProps> = ({ open, handleClose, patient, onClick }) => {
  const procedureId = patient?.procedureData?.id;

  const { data: procedureData } = useQuery(procedureEditFieldsQuery, {
    variables: { id: procedureId },
    skip: !procedureId || !open,
    fetchPolicy: 'network-only',
  });

  const fetchedNote = procedureData?.procedureEditFields?.preOpNote;
  const initialNote = fetchedNote ?? patient?.procedureData?.preOpNote ?? patient?.preOpNote ?? '';

  const [note, setNote] = useState(initialNote);

  const [edit] = useMutation(editProcedurePreOpNote, {
    refetchQueries: procedureId ? [{ query: procedureEditFieldsQuery, variables: { id: procedureId } }] : [],
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    setNote(fetchedNote ?? patient?.procedureData?.preOpNote ?? patient?.preOpNote ?? '');
  }, [open, fetchedNote, patient?.procedureData?.preOpNote, patient?.preOpNote]);

  const onSave = async () => {
    if (onClick) {
      await onClick(note);
    } else {
      // Fix: Check if procedure ID exists before mutation
      if (!procedureId) {
        console.error('Cannot save PreOp note: No procedure ID found for patient', patient);
        return;
      }
      await edit({ variables: { id: procedureId, preOpNote: note } });
    }
    handleClose();
  };

  // Disable save if no procedure data available
  const canSave = onClick || !!procedureId;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      fullWidth
      onClick={e => e.stopPropagation()}
    >
      <DialogTitle id="form-dialog-title">Write PreOp Note</DialogTitle>
      <DialogContent>
        <Box mt={4} display="flex" flexDirection="column">
          <Typography gutterBottom>PreOp Note</Typography>
          <Box mb={2}>
            <TextField
              fullWidth
              label="Note"
              variant="filled"
              multiline
              minRows={1}
              onChange={e => setNote(e.target.value)}
              value={note}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button color="secondary" onClick={onSave} disabled={!canSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProcedurePreOpNoteInput;
